import { useState, useEffect, lazy, Suspense } from "react";
import { SEO } from "@/components/SEO";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import VSLSection from "@/components/VSLSection";
import Framework from "@/components/Framework";
import TransformationNarrative from "@/components/TransformationNarrative";
import HowItWorks from "@/components/HowItWorks";
import Services from "@/components/Services";
import ClientLogos from "@/components/ClientLogos";
import BackToTop from "@/components/BackToTop";
import EnhancedTestimonials from "@/components/EnhancedTestimonials";
import CookieConsent from "@/components/CookieConsent";
import SkipToContent from "@/components/SkipToContent";
import { SectionSkeleton, TestimonialSkeleton } from "@/components/SkeletonLoader";

// Lazy load below-the-fold components for better performance
const Results = lazy(() => import("@/components/Results"));
const About = lazy(() => import("@/components/About"));
const FAQ = lazy(() => import("@/components/FAQ"));
const BookingForm = lazy(() => import("@/components/BookingForm"));
const ContactForm = lazy(() => import("@/components/ContactForm"));
const Footer = lazy(() => import("@/components/Footer"));
const ChatWidget = lazy(() => import("@/components/ChatWidget"));

export default function Index() {
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  return (
    <div id="top" className="bg-deep-navy text-foreground">
      <SEO 
        title="ArqeraAI — AI Transformation Partners | Strategy to Systems"
        description="Strategic AI transformation for business leaders. We identify, implement, and scale intelligent systems that evolve with your business. From strategy to execution — architects of intelligent operations."
        canonical="https://arqeraai.com/"
      />
      <SkipToContent />
      <Navigation activeSection={activeSection} />
      <main id="main-content" tabIndex={-1} className="focus:outline-none">
        <Hero />
        <VSLSection />
        <Framework />
        <TransformationNarrative />
        <HowItWorks />
        <Services />
        <ClientLogos />
      
      {/* Lazy loaded components with suspense fallback */}
      <Suspense fallback={<SectionSkeleton />}>
        <Results />
        <About />
      </Suspense>
      
      <EnhancedTestimonials />
      
      <Suspense fallback={<SectionSkeleton />}>
        <FAQ />
        <BookingForm />
        <ContactForm />
        <Footer />
        <ChatWidget />
      </Suspense>
      </main>
      
      <BackToTop />
      <CookieConsent />
    </div>
  );
}
